// ============================================
// LAYOUT ENUMS
// ============================================
// Using `as const` objects instead of TypeScript enums for better tree-shaking.
// Rolldown/Rollup can eliminate unused const objects but cannot tree-shake enum IIFEs.
// See: specs/HMR.md — Standard VII: Elimination of TypeScript Enums

export const Axis = {
	HORIZONTAL: "HORIZONTAL",
	VERTICAL: "VERTICAL",
} as const;
export type Axis = (typeof Axis)[keyof typeof Axis];

export const Fit = {
	NONE: "NONE",
	LOOSE: "LOOSE",
	TIGHT: "TIGHT",
} as const;
export type Fit = (typeof Fit)[keyof typeof Fit];

export const Stretch = {
	NONE: "NONE",
	HORIZONTAL: "HORIZONTAL",
	VERTICAL: "VERTICAL",
	BOTH: "BOTH",
} as const;
export type Stretch = (typeof Stretch)[keyof typeof Stretch];

export const MainAxisAlignment = {
	CENTER: "CENTER",
	END: "END",
	SPACEAROUND: "SPACEAROUND",
	SPACEBETWEEN: "SPACEBETWEEN",
	SPACEEVENLY: "SPACEEVENLY",
	START: "START",
} as const;
export type MainAxisAlignment = (typeof MainAxisAlignment)[keyof typeof MainAxisAlignment];

export const CrossAxisAlignment = {
	BASELINE: "BASELINE",
	CENTER: "CENTER",
	END: "END",
	START: "START",
	STRETCH: "STRETCH",
} as const;
export type CrossAxisAlignment = (typeof CrossAxisAlignment)[keyof typeof CrossAxisAlignment];

export const MainAxisSize = {
	MIN: "MIN",
	MAX: "MAX",
} as const;
export type MainAxisSize = (typeof MainAxisSize)[keyof typeof MainAxisSize];

export const Alignment = {
	TOP_LEFT: "TOP_LEFT",
	TOP_CENTER: "TOP_CENTER",
	TOP_RIGHT: "TOP_RIGHT",
	CENTER_LEFT: "CENTER_LEFT",
	CENTER: "CENTER",
	CENTER_RIGHT: "CENTER_RIGHT",
	BOTTOM_LEFT: "BOTTOM_LEFT",
	BOTTOM_CENTER: "BOTTOM_CENTER",
	BOTTOM_RIGHT: "BOTTOM_RIGHT",
} as const;
export type Alignment = (typeof Alignment)[keyof typeof Alignment];

// ============================================
// UI ENUMS
// ============================================

export const DialogSize = {
	XS: "XS",
	SM: "SM",
	MD: "MD",
	LG: "LG",
	XL: "XL",
} as const;
export type DialogSize = (typeof DialogSize)[keyof typeof DialogSize];

export const PageContainerType = {
	TABLE: "TABLE",
	FORM: "FORM",
	VIEW: "VIEW",
} as const;
export type PageContainerType = (typeof PageContainerType)[keyof typeof PageContainerType];

export const ButtonStyle = {
	CONTAINED: "contained",
	TEXT: "text",
	OUTLINED: "outlined",
} as const;
export type ButtonStyle = (typeof ButtonStyle)[keyof typeof ButtonStyle];

export const ConfirmationType = {
	NONE: "NONE",
	CONDITIONAL: "CONDITIONAL",
	MANDATORY: "MANDATORY",
} as const;
export type ConfirmationType = (typeof ConfirmationType)[keyof typeof ConfirmationType];

export const Sort = {
	NONE: "NONE",
	ASC: "ASC",
	DESC: "DESC",
} as const;
export type Sort = (typeof Sort)[keyof typeof Sort];

export const FilterOperationType = {
	EQUAL: "EQUAL",
	NOT_EQUAL: "NOT_EQUAL",
	IS_EMPTY: "IS_EMPTY",
	IS_NOT_EMPTY: "IS_NOT_EMPTY",
	LESS: "LESS",
	LESS_OR_EQUAL: "LESS_OR_EQUAL",
	GREATER: "GREATER",
	GREATER_OR_EQUAL: "GREATER_OR_EQUAL",
	LIKE: "LIKE",
} as const;
export type FilterOperationType = (typeof FilterOperationType)[keyof typeof FilterOperationType];

export const CheckboxSelection = {
	AUTO: "AUTO",
	ENABLED: "ENABLED",
	DISABLED: "DISABLED",
} as const;
export type CheckboxSelection = (typeof CheckboxSelection)[keyof typeof CheckboxSelection];

export const TableRepresentation = {
	TABLE: "TABLE",
	TAG: "TAG",
	CARD: "CARD",
} as const;
export type TableRepresentation = (typeof TableRepresentation)[keyof typeof TableRepresentation];

export const MenuLayout = {
	VERTICAL: "VERTICAL",
	HORIZONTAL: "HORIZONTAL",
} as const;
export type MenuLayout = (typeof MenuLayout)[keyof typeof MenuLayout];

export const TabOrientation = {
	HORIZONTAL: "HORIZONTAL",
	VERTICAL: "VERTICAL",
} as const;
export type TabOrientation = (typeof TabOrientation)[keyof typeof TabOrientation];

export const DurationUnit = {
	NANOSECOND: "NANOSECOND",
	MICROSECOND: "MICROSECOND",
	MILLISECOND: "MILLISECOND",
	SECOND: "SECOND",
	MINUTE: "MINUTE",
	HOUR: "HOUR",
	DAY: "DAY",
	WEEK: "WEEK",
	MONTH: "MONTH",
	YEAR: "YEAR",
} as const;
export type DurationUnit = (typeof DurationUnit)[keyof typeof DurationUnit];

export const Placement = {
	TOP: "TOP",
	BOTTOM: "BOTTOM",
	START: "START",
	END: "END",
	DEFAULT: "DEFAULT",
} as const;
export type Placement = (typeof Placement)[keyof typeof Placement];

export const TitleFrom = {
	LABEL: "LABEL",
	ATTRIBUTE: "ATTRIBUTE",
} as const;
export type TitleFrom = (typeof TitleFrom)[keyof typeof TitleFrom];

export const ClaimType = {
	UNDEFINED: "UNDEFINED",
	EMAIL: "EMAIL",
	USERNAME: "USERNAME",
} as const;
export type ClaimType = (typeof ClaimType)[keyof typeof ClaimType];

// ============================================
// DATA ENUMS
// ============================================

export const RelationKind = {
	ASSOCIATION: "ASSOCIATION",
	COMPOSITION: "COMPOSITION",
	AGGREGATION: "AGGREGATION",
	STATIC: "STATIC",
} as const;
export type RelationKind = (typeof RelationKind)[keyof typeof RelationKind];

export const MemberType = {
	STORED: "STORED",
	DERIVED: "DERIVED",
	MAPPED: "MAPPED",
	TRANSIENT: "TRANSIENT",
	ACCESS: "ACCESS",
} as const;
export type MemberType = (typeof MemberType)[keyof typeof MemberType];

export const RelationBehaviourType = {
	LIST: "LIST",
	CREATE: "CREATE",
	SET: "SET",
	UNSET: "UNSET",
	ADD: "ADD",
	REMOVE: "REMOVE",
	RANGE: "RANGE",
	VALIDATE_CREATE: "VALIDATE_CREATE",
	TEMPLATE: "TEMPLATE",
	UPDATE: "UPDATE",
	VALIDATE_UPDATE: "VALIDATE_UPDATE",
	DELETE: "DELETE",
	REFRESH: "REFRESH",
	EXPORT: "EXPORT",
} as const;
export type RelationBehaviourType = (typeof RelationBehaviourType)[keyof typeof RelationBehaviourType];

export const ClassBehaviourType = {
	REFRESH: "REFRESH",
	UPDATE: "UPDATE",
	VALIDATE_UPDATE: "VALIDATE_UPDATE",
	DELETE: "DELETE",
	TEMPLATE: "TEMPLATE",
} as const;
export type ClassBehaviourType = (typeof ClassBehaviourType)[keyof typeof ClassBehaviourType];

export const OperationTypeEnum = {
	MAPPED: "MAPPED",
	STATIC: "STATIC",
} as const;
export type OperationTypeEnum = (typeof OperationTypeEnum)[keyof typeof OperationTypeEnum];

export const OperationTargetBehaviourType = {
	REFRESH: "REFRESH",
	UPDATE: "UPDATE",
	DELETE: "DELETE",
	VALIDATE_INPUT: "VALIDATE_INPUT",
} as const;
export type OperationTargetBehaviourType =
	(typeof OperationTargetBehaviourType)[keyof typeof OperationTargetBehaviourType];
